import { useState } from 'react';
import SidePanel from '@/components/base/SidePanel';
import StarsDisplay from '@/pages/ratings/components/StarsDisplay';
import { driverRatings, type DriverRating } from '@/mocks/ratings';
import EmptyState from '@/components/base/EmptyState';

export default function DriversRatingsTab() {
  const [selectedDriver, setSelectedDriver] = useState<DriverRating | null>(null);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <>
      <div className="space-y-3">
        {driverRatings.length === 0 ? (
          <EmptyState
            icon="ri-e-bike-2-line"
            title="لا توجد تقييمات للمندوبين"
            message="التقييمات ستظهر هنا بعد اكتمال أول طلب عبر مندوب."
          />
        ) : (
          driverRatings.map((driver) => {
            const ratingColor =
              driver.averageStars >= 4.5
                ? 'text-success-600 bg-success-100'
                : driver.averageStars >= 3.5
                ? 'text-accent-700 bg-accent-100'
                : 'text-warning-700 bg-warning-100';

            return (
              <button
                key={driver.driverId}
                type="button"
                onClick={() => setSelectedDriver(driver)}
                className="flex w-full items-center gap-4 rounded-card border border-background-200 bg-white p-4 shadow-card transition-colors hover:border-secondary-300 hover:bg-background-50 cursor-pointer"
              >
                {/* Photo */}
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full border-2 border-background-200">
                  <img
                    src={driver.driverPhoto}
                    alt={driver.driverName}
                    className="h-full w-full object-cover object-top"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 text-right min-w-0">
                  <div className="font-semibold text-[14px] text-foreground-950">
                    {driver.driverName}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <StarsDisplay value={driver.averageStars} size="sm" showNumber />
                    <span className="text-[11px] text-foreground-400">
                      {driver.totalRatings} تقييم
                    </span>
                  </div>
                </div>

                {/* Score badge */}
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[14px] font-bold ${ratingColor}`}
                >
                  {driver.averageStars.toFixed(1)}
                </div>

                {/* Arrow */}
                <span className="flex h-8 w-8 shrink-0 items-center justify-center text-foreground-300">
                  <i className="ri-arrow-left-s-line text-lg" aria-hidden="true"></i>
                </span>
              </button>
            );
          })
        )}
      </div>

      {/* Driver reviews side panel */}
      <SidePanel open={selectedDriver !== null} onClose={() => setSelectedDriver(null)}>
        {selectedDriver && (
          <>
            {/* Panel Header */}
            <div className="flex items-center justify-between border-b border-background-200 p-5">
              <button
                type="button"
                onClick={() => setSelectedDriver(null)}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
                aria-label="إغلاق اللوحة"
              >
                <i className="ri-close-line text-lg" aria-hidden="true"></i>
              </button>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <h3 className="font-semibold text-[15px] text-foreground-950">
                    {selectedDriver.driverName}
                  </h3>
                  <p className="text-[12px] text-foreground-400">
                    {selectedDriver.totalRatings} تقييم
                  </p>
                </div>
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border-2 border-background-200">
                  <img
                    src={selectedDriver.driverPhoto}
                    alt={selectedDriver.driverName}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            {/* Average banner */}
            <div className="flex items-center justify-between bg-secondary-50 px-5 py-3">
              <span className="text-[13px] text-foreground-500">متوسط التقييم</span>
              <div className="flex items-center gap-2">
                <StarsDisplay value={selectedDriver.averageStars} size="sm" />
                <span className="text-[15px] font-bold text-accent-700">
                  {selectedDriver.averageStars.toFixed(1)}
                </span>
              </div>
            </div>

            {/* Reviews */}
            <div className="flex-1 overflow-y-auto p-5">
              {selectedDriver.reviews.length === 0 ? (
                <EmptyState
                  icon="ri-star-line"
                  title="لا توجد تقييمات تفصيلية"
                  message="التقييمات التفصيلية ستظهر هنا عند توفرها."
                />
              ) : (
                <div className="space-y-4">
                  {selectedDriver.reviews.map((review) => (
                    <div
                      key={review.id}
                      className="rounded-card border border-background-200 bg-white p-4"
                    >
                      <div className="flex items-start gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-[13px] font-bold text-secondary-700">
                          {review.clientInitial}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-semibold text-[13px] text-foreground-950">
                              {review.clientName}
                            </span>
                            <StarsDisplay value={review.stars} size="sm" />
                          </div>
                          {review.comment && (
                            <p className="mt-1.5 text-[12px] leading-relaxed text-foreground-600">
                              {review.comment}
                            </p>
                          )}
                          <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-foreground-400">
                            <span className="flex items-center gap-1">
                              <i className="ri-file-list-3-line" aria-hidden="true"></i>
                              {review.orderNo}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-map-pin-2-line" aria-hidden="true"></i>
                              {review.taskType}
                            </span>
                            <span>{formatDate(review.date)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </SidePanel>
    </>
  );
}